import { SocketClient } from "cbcore-ts"
import { NO, YES } from "uicore-ts"
import {
    RHCompanyUnseenCounts,
    RHNotificationAcknowledgementState,
    RHOfferNotificationSnapshot,
    RHQANotificationSnapshot,
    RHUnseenCounts
} from "../SocketClientFunctions"
import { RHCore } from "./RHCore"
import { RHFeatureFlags } from "./RHFeatureFlags"
import { RHSubscriptionBase } from "./RHSubscriptionBase"

interface TrackedNotification {
    notificationId: string
    procurementId: string
    companyId: string
    applicationMode: "procurer" | "offerer"
    eventType: string
    tab: string
    creationDate: string
    contentId?: string
}

interface NotificationReadingContent {
    procurementId: string
    tab?: string
    eventTypes?: string[]
    qaSnapshot?: RHQANotificationSnapshot
    offerSnapshot?: RHOfferNotificationSnapshot
    auditCutoffDate?: string
    messageIds?: string[]
}


// ============================================================================
// RHNotificationStore
//
// Single source of truth for ALL badge counts in the client — both
// per-entity row badges and user-wide nav-level totals.
//
// ## Per-entity counts
//
//   _notificationCounts  — unseen notification presentation on procurement rows,
//                          cleared by markSeen(entityId). This is the COMBINED
//                          Q&A + offer + procurement-update count; the three
//                          family-specific maps hold the exact split, last
//                          delivered by an acknowledgement response/push —
//                          undefined until the first one arrives this session.
//   _eventCounts         — "unseen event log" badges on procurement rows,
//                          cleared by markEventsSeen(entityId)
//   _offerMessageCounts  — unread offer-thread message counts, keyed BOTH by
//                          procurementId (row titles in the main tables) and by
//                          procurementOfferId (details-view tab/accordion titles).
//                          The two key spaces are disjoint ObjectIds and are
//                          loaded together by reloadOfferMessageCounts(), which
//                          REPLACES the map so counts that dropped to zero
//                          clear. Cleared on logout via clearOfferMessageCounts().
//
// ## Global counts
//
//   _countsByCompanyId   — RHUnseenCounts per company, driving nav button
//                          badges for the ACTIVE company only. RHCore.unseenCounts
//                          is a computed property that delegates here (via
//                          globalCounts, which reads the active company's
//                          bucket). Updated by updateGlobalCounts(), which
//                          receives a company-scoped RHCompanyUnseenCounts so a
//                          background company's activity never lights up the
//                          currently active company's badges for a
//                          multi-company user. Each company's snapshot carries
//                          its own notificationStateEpoch/Revision (see below),
//                          tracked per companyId so two companies' independent
//                          update streams can never be compared against each
//                          other's ordering state.
//
// ## Acknowledgement ordering and dedup
//
//   A "mark seen" call and a concurrent server push (e.g. from another open
//   tab) can race. _notificationStateEpochByCompanyId /
//   _notificationStateRevisionByCompanyId record the last-applied snapshot's
//   ordering metadata per company so a delayed, older response can never
//   overwrite newer state (see _serverSnapshotOrder). _appliedAcknowledgementIds
//   / _appliedAcknowledgementIdOrder deduplicate the same acknowledgement being
//   applied twice (once from the initiating call's own response, once from the
//   server's push to this session).
//
// ## Why this is the single source of truth
//
// Previously RHCore._unseenCounts and RHNotificationStore._counts were
// updated by different code paths and could diverge — a row badge could
// be cleared while the nav badge was stale or vice versa. Now every path
// that changes any badge count goes through this store only.
//
// ## Subscribing
//
//   store.subscribe(this, affectedIds => { ... })
//   store.unsubscribe(this)
//
// Subscribe in viewWillAppear, unsubscribe in viewWillDisappear.
// ============================================================================

export class RHNotificationStore extends RHSubscriptionBase<(affectedEntityIds: string[]) => void> {

    _pendingReading?: TrackedNotification & {
        userId: string
        isMarkingAsRead: boolean
    }

    get hasPendingNotificationReading(): boolean {
        return this._pendingReading !== undefined
    }

    beginTrackingReadingOfNotification(notification: TrackedNotification) {
        this._pendingReading = undefined
        const userId = this._core.userProfile?._id?.toString()
        if (!userId) {
            return
        }
        this._pendingReading = { ...notification, userId, isMarkingAsRead: NO }
    }

    markPendingNotificationAsReadIfContentMatches(content: NotificationReadingContent) {
        const pending = this._pendingReading
        if (!pending || pending.isMarkingAsRead || pending.procurementId !== content.procurementId ||
            pending.companyId !== this._activeCompanyId() || pending.userId !== this._core.userProfile?._id?.toString()) return
        if (pending.applicationMode !== this._core.applicationMode) return
        if (content.tab !== undefined && content.tab !== pending.tab) return
        if (content.eventTypes && !content.eventTypes.includes(pending.eventType)) return
        const cutoffDate = content.auditCutoffDate ?? content.qaSnapshot?.auditCutoff ?? content.offerSnapshot?.auditCutoff
        const notificationTimestamp = new Date(pending.creationDate).getTime()
        if (!Number.isFinite(notificationTimestamp) || (!cutoffDate && !content.messageIds)) return
        if (cutoffDate !== undefined) {
            const cutoffTimestamp = new Date(cutoffDate).getTime()
            if (!Number.isFinite(cutoffTimestamp) || notificationTimestamp > cutoffTimestamp) return
        }
        if (content.qaSnapshot) {
            let contentIds: string[] = []
            if (pending.eventType === "question.asked") contentIds = content.qaSnapshot.questionIds
            if (pending.eventType === "question.answered") contentIds = content.qaSnapshot.answeredQuestionIds
            if (pending.eventType === "procurerBroadcast.sent") contentIds = content.qaSnapshot.broadcastIds
            if (pending.eventType === "procurement.amended") contentIds = content.qaSnapshot.amendmentIds ?? []
            if (!pending.contentId || !contentIds.includes(pending.contentId)) return
        }
        if (content.offerSnapshot && (!pending.contentId || !content.offerSnapshot.offerIds.includes(pending.contentId))) return
        if (content.messageIds && (!pending.contentId || !content.messageIds.includes(pending.contentId))) return
        pending.isMarkingAsRead = YES
        void SocketClient.MarkNotificationAsRead({
            notificationId: pending.notificationId, companyId: pending.companyId, applicationMode: pending.applicationMode
        }).then(() => {
            if (this._pendingReading === pending) this._pendingReading = undefined
        }).catch(error => {
            pending.isMarkingAsRead = NO
            console.error("RHNotificationStore: notification could not be marked as read", error)
        })
    }

    _notificationCounts: Record<string, number> = {}
    _qaNotificationCounts: Record<string, number> = {}
    _offerNotificationCounts: Record<string, number> = {}
    _procurementNotificationCounts: Record<string, number> = {}
    _eventCounts: Record<string, number> = {}
    _offerMessageCounts: Record<string, number> = {}
    _countsByCompanyId: Record<string, RHUnseenCounts> = {}
    _notificationStateEpochByCompanyId: Record<string, string | undefined> = {}
    _notificationStateRevisionByCompanyId: Record<string, number> = {}
    _appliedAcknowledgementIds = new Set<string>()
    _appliedAcknowledgementIdOrder: string[] = []
    _notificationStateGeneration = 0
    _core: RHCore


    constructor(core: RHCore) {
        super()
        this._core = core
    }


    // =========================================================================
    // Read API — per-entity
    // =========================================================================

    /**
     * Returns the current unseen notification count for an entity.
     * Returns 0 if no count has been recorded for this entity yet.
     */
    notificationCount(entityId: string): number {
        return this._notificationCounts[entityId] ?? 0
    }


    /**
     * Returns the last exact Q&A notification count delivered by an
     * acknowledgement response/push. Undefined means no split count snapshot
     * has been received for this procurement in the current session.
     */
    qaNotificationCount(entityId: string): number | undefined {
        return this._qaNotificationCounts[entityId]
    }


    /**
     * Returns the last exact offer notification count delivered by an
     * acknowledgement response/push. Undefined means no split count snapshot
     * has been received for this procurement in the current session.
     */
    offerNotificationCount(entityId: string): number | undefined {
        return this._offerNotificationCounts[entityId]
    }


    /** Returns the last exact published-procurement update count. */
    procurementNotificationCount(entityId: string): number | undefined {
        return this._procurementNotificationCounts[entityId]
    }


    /** Invalidates notification-bearing responses that started before an acknowledgement. */
    get notificationStateGeneration(): number {
        return this._notificationStateGeneration
    }


    /**
     * Returns the current unseen event log count for an entity.
     * Returns 0 if no count has been recorded for this entity yet.
     */
    eventCount(entityId: string): number {
        return this._eventCounts[entityId] ?? 0
    }


    /**
     * Returns the current unread offer-thread message count for an entity.
     * Accepts BOTH a procurementId (per-row titles) and a procurementOfferId
     * (details-view titles) — the map carries both key spaces.
     * Returns 0 if no count has been loaded for this entity yet.
     */
    offerMessageCount(procurementOrOfferId: string): number {
        // While the private-messaging UI is hidden, suppress every
        // unread-message presentation at the single source instead of in each view.
        if (!RHFeatureFlags.offerMessagingIsVisible) {
            return 0
        }
        return this._offerMessageCounts[procurementOrOfferId] ?? 0
    }

    // =========================================================================
    // Read API — global
    // =========================================================================

    /**
     * Returns the unseen counts for the active company. RHCore.unseenCounts
     * delegates here. Returns all-zeros before the first login, before an
     * active company is known, or before counts for that company have been
     * received from the server.
     */
    get globalCounts(): RHUnseenCounts {
        const companyId = this._activeCompanyId()
        if (!companyId) {
            return RHNotificationStore._zeroGlobalCounts()
        }
        return this._countsByCompanyId[companyId] ?? RHNotificationStore._zeroGlobalCounts()
    }


    /**
     * Returns the unseen counts for a specific company, regardless of which
     * company is currently active. Returns all-zeros if nothing has been
     * loaded for that company yet.
     */
    countsForCompany(companyId: string): RHUnseenCounts {
        return this._countsByCompanyId[companyId] ?? RHNotificationStore._zeroGlobalCounts()
    }


    _activeCompanyId(): string | undefined {
        return this._core.companyAffiliationStore.activeCompanyId
    }


    // =========================================================================
    // Write API — per-entity, called by domain stores and fetchers
    // =========================================================================

    /**
     * Records the unseen notification count for an entity.
     * Call this whenever fetched data carries an embedded
     * unseenNotificationCount field.
     * Does nothing and does not notify if the count is unchanged.
     */
    setCount(entityId: string, count: number) {
        const current = this._notificationCounts[entityId] ?? 0
        if (current === count) {
            return
        }
        this._notificationCounts[entityId] = count
        this._notifySubscribers([entityId])
    }


    /**
     * Bulk variant of setCount. Accepts a map of entityId → count.
     * Notifies subscribers once with all affected IDs.
     * Silently skips entries whose count has not changed.
     */
    setCounts(counts: Record<string, number>) {
        const affected: string[] = []
        for (const [entityId, count] of Object.entries(counts)) {
            const current = this._notificationCounts[entityId] ?? 0
            if (current !== count) {
                this._notificationCounts[entityId] = count
                affected.push(entityId)
            }
        }
        if (affected.length > 0) {
            this._notifySubscribers(affected)
        }
    }


    /**
     * Reloads the unread offer-thread message counts from the server and
     * REPLACES the whole map. The server returns the same unread messages under
     * two key spaces, both of which have consumers:
     *   - procurementId keys → bold row action titles in the main tables
     *   - procurementOfferId keys → the bold details-view "Minu pakkumus" tab and
     *     per-part "Privaatsed sõnumid" accordion title
     * The two key spaces are disjoint (different ObjectIds), so they are merged
     * into one map.
     *
     * Replacing (instead of merging into the old map) is essential: the server
     * aggregations only return procurements/offers with a non-zero count, so a
     * count that drops to zero simply disappears from the response — with a
     * merge the stale non-zero value would stay and the unread emphasis would never clear.
     *
     * Call after any event that changes unread state: viewDidAppear of the main
     * views, an OfferMessageReceived push, and after MarkOfferMessagesAsRead.
     * Fire-and-forget safe — does not throw, logs warnings on failure.
     */
    reloadOfferMessageCounts(): void {
        // No consumer can show these counts while messaging is hidden —
        // skip the two server round-trips entirely.
        if (!RHFeatureFlags.offerMessagingIsVisible) {
            return
        }
        Promise.all([
            SocketClient.GetUnreadOfferMessageCountsByProcurement(),
            SocketClient.GetUnreadOfferMessageCountsForCurrentUser()
        ]).then(([byProcurementResponse, byOfferResponse]) => {
            this._replaceOfferMessageCounts({
                ...byProcurementResponse.result.counts,
                ...byOfferResponse.result.counts
            })
        }).catch(error => {
            console.warn("RHNotificationStore: reloadOfferMessageCounts failed", error)
        })
    }


    /**
     * Replaces the unread offer-message count map with a fresh server snapshot.
     * Keys missing from the new snapshot are treated as 0 (read in the
     * meantime) and their subscribers are notified so stale unread emphasis clears.
     */
    _replaceOfferMessageCounts(counts: Record<string, number>) {
        const affected: string[] = []
        for (const [entityId, count] of Object.entries(counts)) {
            const current = this._offerMessageCounts[entityId] ?? 0
            if (current !== count) {
                affected.push(entityId)
            }
        }
        for (const entityId of Object.keys(this._offerMessageCounts)) {
            if (!(entityId in counts) && this._offerMessageCounts[entityId] !== 0) {
                affected.push(entityId)
            }
        }
        this._offerMessageCounts = { ...counts }
        if (affected.length > 0) {
            this._notifySubscribers(affected)
        }
    }


    /** Clears user-specific per-procurement notification state at logout. */
    clearNotificationCounts() {
        const affectedEntityIds = [...new Set([
            ...Object.keys(this._notificationCounts),
            ...Object.keys(this._qaNotificationCounts),
            ...Object.keys(this._offerNotificationCounts),
            ...Object.keys(this._procurementNotificationCounts)
        ])]
        this._notificationCounts = {}
        this._qaNotificationCounts = {}
        this._offerNotificationCounts = {}
        this._procurementNotificationCounts = {}
        this._appliedAcknowledgementIds.clear()
        this._appliedAcknowledgementIdOrder = []
        this._notificationStateEpochByCompanyId = {}
        this._notificationStateRevisionByCompanyId = {}
        this._notificationStateGeneration += 1
        this._core.procurementStore.invalidateNotificationStateCaches(affectedEntityIds)
        if (affectedEntityIds.length > 0) {
            this._notifySubscribers(affectedEntityIds)
        }
    }


    /** Procurement IDs can be shared across companies, while these maps represent only the active one. */
    activeCompanyDidChange() {
        this._pendingReading = undefined
        const affectedEntityIds = [...new Set([
            ...Object.keys(this._notificationCounts),
            ...Object.keys(this._qaNotificationCounts),
            ...Object.keys(this._offerNotificationCounts),
            ...Object.keys(this._procurementNotificationCounts)
        ])]
        this._notificationCounts = {}
        this._qaNotificationCounts = {}
        this._offerNotificationCounts = {}
        this._procurementNotificationCounts = {}
        this._notificationStateGeneration += 1
        this._core.procurementStore.invalidateNotificationStateCaches(affectedEntityIds)
        if (affectedEntityIds.length > 0) {
            this._notifySubscribers(affectedEntityIds)
        }
    }


    /**
     * Clears all per-company global counts and notifies subscribers. Called on
     * logout so stale counts are not shown to a subsequently logged-in user.
     */
    clearAllCounts() {
        this._pendingReading = undefined
        this._countsByCompanyId = {}
        this._notifySubscribers([])
        this._core.broadcastGlobalCountsDidChange(NO)
    }


    /**
     * Clears all per-procurement offer-message counts and notifies subscribers.
     * Called on logout so stale counts are not shown to a subsequently logged-in user.
     */
    clearOfferMessageCounts() {
        const affectedIds = Object.keys(this._offerMessageCounts)
        this._offerMessageCounts = {}
        if (affectedIds.length > 0) {
            this._notifySubscribers(affectedIds)
        }
    }


    // =========================================================================
    // Write API — global counts
    // =========================================================================

    /**
     * Replaces the unseen counts for one company and notifies subscribers.
     * Called by RHCore whenever the server pushes UnseenCountsDidChange,
     * and after any mark-seen call resolves. Ignored if the company's
     * embedded ordering metadata shows this snapshot is older than one
     * already applied (see _serverSnapshotOrder).
     *
     * Replaces the old RHCore.updateUnseenCounts() — that method now
     * delegates here.
     */
    updateGlobalCounts(companyCounts: RHCompanyUnseenCounts) {
        const snapshotOrder = this._serverSnapshotOrder(companyCounts.companyId, companyCounts.counts)
        if (snapshotOrder !== "new") {
            return
        }
        this._replaceCompanyCounts(companyCounts.companyId, companyCounts.counts, YES)
    }


    /** Applies a local UI-only count patch for one company without advancing server ordering. */
    patchGlobalCounts(companyId: string, counts: Partial<RHUnseenCounts>) {
        this._replaceCompanyCounts(companyId, { ...this.countsForCompany(companyId), ...counts }, YES)
    }


    /**
     * Applies the authoritative result of a notification acknowledgement for
     * one company. The order is deliberate:
     *
     * 1. replace exact per-procurement counts;
     * 2. invalidate list caches that embed the old counts;
     * 3. replace the company's global counts and broadcast their change;
     * 4. notify badge subscribers once with all affected procurement IDs.
     *
     * The same method handles the initiating socket response and the server
     * push sent to the user's other active sessions.
     */
    applyAcknowledgementState(state: RHNotificationAcknowledgementState) {
        if (state.acknowledgementId) {
            if (this._appliedAcknowledgementIds.has(state.acknowledgementId)) {
                return
            }
            this._appliedAcknowledgementIds.add(state.acknowledgementId)
            this._appliedAcknowledgementIdOrder.push(state.acknowledgementId)
            let oldestAcknowledgementId: string | undefined
            if (this._appliedAcknowledgementIdOrder.length > 100) {
                oldestAcknowledgementId = this._appliedAcknowledgementIdOrder.shift()
            }
            if (oldestAcknowledgementId) {
                this._appliedAcknowledgementIds.delete(oldestAcknowledgementId)
            }
        }

        const snapshotOrder = this._serverSnapshotOrder(state.companyId, state.unseenCounts)
        if (snapshotOrder === "stale") {
            return
        }
        if (state.companyId !== this._activeCompanyId()) {
            this._replaceCompanyCounts(state.companyId, state.unseenCounts, NO)
            return
        }
        this._notificationStateGeneration += 1

        const affectedEntityIds = Object.keys(state.perProcurementUnseenCounts ?? {})

        for (const entityId of affectedEntityIds) {
            const splitCounts = state.perProcurementUnseenCounts[entityId]
            const qaCount = RHNotificationStore._normalisedCount(splitCounts.unseenQANotificationCount)
            const offerCount = RHNotificationStore._normalisedCount(splitCounts.unseenOfferNotificationCount)
            const procurementCount = RHNotificationStore._normalisedCount(
                splitCounts.unseenProcurementNotificationCount
            )

            this._qaNotificationCounts[entityId] = qaCount
            this._offerNotificationCounts[entityId] = offerCount
            this._procurementNotificationCounts[entityId] = procurementCount
            this._notificationCounts[entityId] = qaCount + offerCount + procurementCount
        }

        this._core.procurementStore.invalidateNotificationStateCaches(affectedEntityIds)

        // These lists embed notification metadata but their actual offer
        // content did not change. Patch the cached rows silently; the single
        // notification-store broadcast below redraws visible list data without
        // waking offer-content subscribers such as the evaluation view.
        this._core.offerStore.applyIncomingOfferNotificationCounts(
            state.perProcurementUnseenCounts
        )
        this._core.offerStore.applyOutgoingOfferNotificationCounts(
            state.perProcurementUnseenCounts
        )

        this._replaceCompanyCounts(state.companyId, state.unseenCounts, NO)
        this._notifySubscribers(affectedEntityIds)
    }


    _replaceCompanyCounts(companyId: string, counts: RHUnseenCounts, notifySubscribers: boolean) {
        // While the private-messaging UI is hidden, zero its nav-level counters
        // so the "Esitatud pakkumused" unread emphasis cannot point at hidden UI.
        if (!RHFeatureFlags.offerMessagingIsVisible) {
            counts = {
                ...counts,
                unreadOfferMessages: 0,
                unreadOfferMessagesAsOfferer: 0
            }
        }
        const previousPendingCount = this._countsByCompanyId[companyId]?.pendingJoinRequests
        this._countsByCompanyId[companyId] = counts

        // Notify all badge subscribers — global count change may affect
        // any entity's displayed badge.
        if (notifySubscribers) {
            this._notifySubscribers([])
        }

        // Fire the view-tree broadcasts that views depend on for nav badges —
        // only meaningful when the changed company is the active one, since
        // globalCounts only ever reflects the active company's bucket.
        if (companyId === this._activeCompanyId()) {
            this._core.broadcastGlobalCountsDidChange(
                counts.pendingJoinRequests !== previousPendingCount
            )
        }
    }


    // =========================================================================
    // Write API — mark seen
    // =========================================================================

    /**
     * Marks notification log entries for the given entity as seen.
     * An optional eventTypes filter narrows the marking to specific event
     * types (e.g. the Q&A tab passes question.* types and the offers tab
     * passes offer.submitted, so neither clears the other's badge).
     * Q&A callers also pass the loaded content snapshot so a concurrent,
     * not-yet-rendered answer or message cannot be acknowledged accidentally.
     * Optimistically sets the local count and notifies subscribers,
     * then calls MarkNotificationEntriesAsSeen on the server and updates
     * global counts from the response.
     *
     * The per-entity count here is the COMBINED count across event types.
     * A type-filtered marking therefore must NOT zero it — the caller knows
     * how many notifications of the OTHER types remain and passes that via
     * remainingCount, so sibling markers (e.g. the offers-tab emphasis while the
     * Q&A tab is read) survive the optimistic update.
     * Throws if the server call fails.
     */
    async markSeen(
        entityId: string,
        eventTypes?: string[],
        remainingCount: number = 0,
        qaSnapshot?: RHQANotificationSnapshot,
        offerSnapshot?: RHOfferNotificationSnapshot,
        auditCutoff?: string
    ) {
        this._notificationCounts[entityId] = remainingCount
        this._notifySubscribers([entityId])

        const companyId = this._activeCompanyId()
        const result = (await SocketClient.MarkNotificationEntriesAsSeen(
            { procurementId: entityId, eventTypes, companyId, qaSnapshot, offerSnapshot, auditCutoff }
        )).result

        this._applyAcknowledgementResult(companyId, result)
    }


    /**
     * Marks specific notification entries as seen from the "Minu teatised"
     * inbox. The server applies the same acknowledgement to contextual badges
     * and pushes fresh global counts to every active session of this user.
     * Throws if the server call fails — the caller decides how to handle it.
     */
    async markInboxEntriesSeen(logIds: string[]) {
        const companyId = this._activeCompanyId()
        const result = (await SocketClient.MarkNotificationInboxEntriesAsSeen(
            { logIds, companyId }
        )).result
        this._applyAcknowledgementResult(companyId, result)
    }


    /**
     * Marks ALL of the user's unseen notification-INBOX entries as seen
     * ("Märgi kõik teavitused nähtuks"). Updates global counts from the response.
     * Throws if the server call fails — the caller decides how to handle it.
     */
    async markAllInboxNotificationsSeen() {
        const companyId = this._activeCompanyId()
        const result = (await SocketClient.MarkNotificationInboxEntriesAsSeen(
            { all: YES, companyId }
        )).result
        this._applyAcknowledgementResult(companyId, result)
    }


    /**
     * Re-fetches the active company's unseen counts from the server WITHOUT
     * marking anything as seen, and applies them. Used by the main views on
     * entry so nav badges also pick up purely time-based changes (e.g. the
     * deadline-gated incoming-offers count) that no push message covers.
     * Fire-and-forget safe — does not throw, logs warnings on failure.
     */
    refreshGlobalCounts(): void {
        SocketClient.RetrieveUnseenCounts({ companyId: this._activeCompanyId() }).then(response => {
            this.updateGlobalCounts(response.result.unseenCounts)
        }).catch(error => {
            console.warn("RHNotificationStore: RetrieveUnseenCounts failed", error)
        })
    }


    markPublishedProcurementsSeen(): void {
        SocketClient.MarkPublishedProcurementsAsSeen({ companyId: this._activeCompanyId() }).then(response => {
            this.updateGlobalCounts(response.result.unseenCounts)
        }).catch(error => {
            console.warn("RHNotificationStore: MarkPublishedProcurementsAsSeen failed", error)
        })
    }


    /**
     * Marks all event log entries for the given entity as seen.
     * Optimistically clears the local event count to 0 and notifies subscribers,
     * then calls MarkEventLogEntriesAsSeen on the server and updates global
     * counts from the response.
     * Fire-and-forget safe — does not throw, logs warnings on failure.
     */
    markEventsSeen(entityId: string): void {
        this._eventCounts[entityId] = 0
        this._notifySubscribers([entityId])

        SocketClient.MarkEventLogEntriesAsSeen(
            { procurementId: entityId, companyId: this._activeCompanyId() }
        ).then(response => {
            this.updateGlobalCounts(response.result.unseenCounts)
        }).catch(error => {
            console.warn("RHNotificationStore: MarkEventLogEntriesAsSeen failed", error)
        })
    }


    // =========================================================================
    // Static helpers
    // =========================================================================

    static _zeroGlobalCounts(): RHUnseenCounts {
        return {
            totalEvents: 0,
            incomingOffers: 0,
            incomingQuestions: 0,
            outgoingOfferUpdates: 0,
            newPublishedProcurements: 0,
            procurementsRequiringProcurerAttention: 0,
            procurementsRequiringQAAttention: 0,
            offersRequiringOffererAttention: 0,
            pendingJoinRequests: 0,
            unreadOfferMessages: 0,
            unreadOfferMessagesAsOfferer: 0,
            unseenEvents: 0,
            unseenAnsweredQuestions: 0,
            unseenProcurementUpdates: 0
        }
    }


    /**
     * Accepts both the new acknowledgement response and an older server's
     * counts-only response during a rolling deployment. companyId is
     * undefined only for a single-company user whose acting company could
     * not be resolved client-side; the server still resolves and stamps its
     * own companyId onto a full acknowledgement response.
     */
    _applyAcknowledgementResult(
        companyId: string | undefined,
        result: { unseenCounts: RHUnseenCounts, companyId?: string }
    ) {
        const acknowledgementState = result as Partial<RHNotificationAcknowledgementState>
        if (acknowledgementState.perProcurementUnseenCounts && acknowledgementState.companyId) {
            this.applyAcknowledgementState(acknowledgementState as RHNotificationAcknowledgementState)
            return
        }
        const resolvedCompanyId = result.companyId ?? companyId
        if (!resolvedCompanyId) {
            return
        }
        this.updateGlobalCounts({ companyId: resolvedCompanyId, counts: result.unseenCounts })
    }


    static _normalisedCount(value: number): number {
        if (!Number.isFinite(value)) {
            return 0
        }
        return Math.max(0, Math.floor(value))
    }


    /**
     * Classifies and records one company's server snapshot ordering metadata.
     * Versionless snapshots remain accepted until this client has seen a
     * versioned server for that company.
     */
    _serverSnapshotOrder(companyId: string, counts: RHUnseenCounts): "new" | "same" | "stale" {
        const epoch = counts.notificationStateEpoch
        const revision = counts.notificationStateRevision
        const hasValidVersion = typeof epoch === "string" && epoch.length > 0 &&
            typeof revision === "number" && Number.isSafeInteger(revision) && revision > 0

        const previousEpoch = this._notificationStateEpochByCompanyId[companyId]
        const previousRevision = this._notificationStateRevisionByCompanyId[companyId] ?? 0

        if (!hasValidVersion) {
            if (previousEpoch) {
                return "stale"
            }
            return "new"
        }

        if (previousEpoch !== epoch) {
            this._notificationStateEpochByCompanyId[companyId] = epoch
            this._notificationStateRevisionByCompanyId[companyId] = revision
            return "new"
        }
        if (revision < previousRevision) {
            return "stale"
        }
        if (revision === previousRevision) {
            return "same"
        }
        this._notificationStateRevisionByCompanyId[companyId] = revision
        return "new"
    }

}
